/**
 * useValidation.js
 * React 관리자 시스템용 폼 검증 훅
 * SI-05 Phase 8: 관리자 로그인 화면 통합 검증 시스템
 */

import { useState, useCallback, useEffect } from 'react';
import { sanitizeString, sanitizeObject, validateXssPrevention, sanitizeApiRequest } from '../utils/xssProtection';

/**
 * 통합 폼 검증 훅
 * @param {Object} schema - Yup 검증 스키마
 * @param {Object} options - 검증 옵션
 * @returns {Object} 검증 상태 및 함수들
 */
export const useValidation = (schema, options = {}) => {
  const {
    sanitizeMode = 'default',
    enableXssDetection = true,
    validateOnChange = true,
    validateOnBlur = true,
    debounceMs = 300
  } = options;

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [xssRisks, setXssRisks] = useState({});
  const [securityEvents, setSecurityEvents] = useState([]);

  // 디바운스 타이머
  const [debounceTimer, setDebounceTimer] = useState(null);

  /**
   * XSS 위험도 평가 및 로깅
   */
  const assessXssRisk = useCallback((fieldName, originalValue, sanitizedValue) => {
    if (!enableXssDetection) return null;

    const validation = validateXssPrevention(originalValue, sanitizedValue);
    
    if (validation.hasSuspiciousContent || !validation.isClean) {
      const event = {
        timestamp: new Date().toISOString(),
        field: fieldName,
        riskLevel: validation.riskLevel,
        originalValue: originalValue.substring(0, 100), // 보안상 일부만 로깅
        detectedPatterns: validation.detectedPatterns.map(p => p.toString()),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      setSecurityEvents(prev => [...prev, event]);
      
      // 콘솔에 보안 이벤트 로깅
      console.warn('XSS Risk Detected:', {
        field: fieldName,
        riskLevel: validation.riskLevel,
        patterns: validation.detectedPatterns.length
      });

      return validation;
    }

    return null;
  }, [enableXssDetection]);

  /**
   * 필드 값 설정 (XSS 정화 포함)
   */
  const setValue = useCallback((fieldName, value) => {
    const sanitizedValue = sanitizeString(value, sanitizeMode);
    
    // XSS 위험도 평가
    const xssValidation = assessXssRisk(fieldName, value, sanitizedValue);
    
    setValues(prev => ({
      ...prev,
      [fieldName]: sanitizedValue
    }));

    if (xssValidation) {
      setXssRisks(prev => ({
        ...prev,
        [fieldName]: xssValidation
      }));
    } else {
      setXssRisks(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }

    // 실시간 검증
    if (validateOnChange) {
      clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        validateField(fieldName, sanitizedValue);
      }, debounceMs);
      setDebounceTimer(timer);
    }
  }, [sanitizeMode, validateOnChange, debounceMs, assessXssRisk]);

  /**
   * 개별 필드 검증
   */
  const validateField = useCallback(async (fieldName, value) => {
    if (!schema) return;

    try {
      await schema.validateAt(fieldName, { [fieldName]: value });
      
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: error.message
      }));
    }
  }, [schema]);

  /**
   * 전체 폼 검증
   */
  const validateForm = useCallback(async () => {
    if (!schema) return { isValid: true, errors: {} };

    setIsValidating(true);
    
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      setIsValid(true);
      return { isValid: true, errors: {} };
    } catch (error) {
      const validationErrors = {};
      
      if (error.inner) {
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
        });
      }
      
      setErrors(validationErrors);
      setIsValid(false);
      return { isValid: false, errors: validationErrors };
    } finally {
      setIsValidating(false);
    }
  }, [schema, values]);

  /**
   * 필드 터치 상태 설정
   */
  const setFieldTouched = useCallback((fieldName, isTouched = true) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: isTouched
    }));

    if (validateOnBlur && isTouched) {
      validateField(fieldName, values[fieldName]);
    }
  }, [validateOnBlur, values, validateField]);

  /**
   * 폼 초기화
   */
  const resetForm = useCallback(() => {
    setValues({});
    setErrors({});
    setTouched({});
    setXssRisks({});
    setSecurityEvents([]);
    setIsValid(false);
  }, []);

  /**
   * 안전한 폼 데이터 반환 (API 전송용)
   */
  const getSafeFormData = useCallback((endpoint = '') => {
    return sanitizeApiRequest(values, endpoint);
  }, [values]);

  // 전체 폼 유효성 상태 업데이트
  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasValues = Object.keys(values).length > 0;
    setIsValid(hasValues && !hasErrors);
  }, [errors, values]);

  return {
    // 상태
    values,
    errors,
    touched,
    isValidating,
    isValid,
    xssRisks,
    securityEvents,
    
    // 함수
    setValue,
    validateField,
    validateForm,
    setFieldTouched,
    resetForm,
    getSafeFormData,
    
    // 헬퍼 함수
    getFieldProps: (fieldName) => ({
      value: values[fieldName] || '',
      onChange: (e) => setValue(fieldName, e.target.value),
      onBlur: () => setFieldTouched(fieldName),
      error: touched[fieldName] && errors[fieldName],
      hasXssRisk: !!xssRisks[fieldName]
    }),
    
    // 보안 상태
    hasSecurityRisks: Object.keys(xssRisks).length > 0,
    securityEventCount: securityEvents.length
  };
};

/**
 * 관리자 인증 전용 검증 훅
 */
export const useAuthValidation = () => {
  const [adminId, setAdminId] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [securityEvents, setSecurityEvents] = useState([]);

  /**
   * 관리자 ID 검증 및 정화
   */
  const setAdminIdSafe = useCallback((value) => {
    // 관리자 ID는 텍스트만 허용
    const sanitized = sanitizeString(value, 'textOnly');
    
    // XSS 시도 감지
    const validation = validateXssPrevention(value, sanitized);
    if (validation && validation.hasSuspiciousContent) {
      const event = {
        timestamp: new Date().toISOString(),
        field: 'adminId',
        riskLevel: validation.riskLevel,
        attempt: value.substring(0, 50),
        userAgent: navigator.userAgent
      };
      
      setSecurityEvents(prev => [...prev, event]);
      console.warn('Admin ID XSS attempt detected:', validation.riskLevel);
    }
    
    setAdminId(sanitized);
    
    // 기본 검증
    if (!sanitized.trim()) {
      setErrors(prev => ({ ...prev, adminId: '아이디를 입력해주세요.' }));
    } else if (sanitized.length < 3) {
      setErrors(prev => ({ ...prev, adminId: '아이디는 3자 이상이어야 합니다.' }));
    } else if (!/^[a-zA-Z0-9_-]+$/.test(sanitized)) {
      setErrors(prev => ({ ...prev, adminId: '아이디는 영문, 숫자, _, - 만 사용 가능합니다.' }));
    } else {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated.adminId;
        return updated;
      });
    }
  }, []);

  /**
   * 비밀번호 검증 (정화하지 않음 - 해시될 예정)
   */
  const setPasswordSafe = useCallback((value) => {
    setPassword(value);
    
    // 기본 검증만 수행 (XSS 정화는 하지 않음)
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, password: '비밀번호를 입력해주세요.' }));
    } else if (value.length < 4) {
      setErrors(prev => ({ ...prev, password: '비밀번호는 4자 이상이어야 합니다.' }));
    } else {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated.password;
        return updated;
      });
    }
  }, []);

  /**
   * 로그인 데이터 검증 및 정화
   */
  const validateLoginData = useCallback(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasRequiredFields = adminId.trim() && password.trim();
    
    if (!hasRequiredFields) {
      if (!adminId.trim()) {
        setErrors(prev => ({ ...prev, adminId: '아이디를 입력해주세요.' }));
      }
      if (!password.trim()) {
        setErrors(prev => ({ ...prev, password: '비밀번호를 입력해주세요.' }));
      }
      return { isValid: false, errors };
    }
    
    return {
      isValid: !hasErrors && hasRequiredFields,
      errors,
      data: {
        adminId: adminId.trim(), // 이미 정화됨
        password: password.trim() // 원본 유지 (해시될 예정)
      },
      securityEvents
    };
  }, [adminId, password, errors, securityEvents]);

  /**
   * 폼 초기화
   */
  const resetAuthForm = useCallback(() => {
    setAdminId('');
    setPassword('');
    setErrors({});
    setSecurityEvents([]);
  }, []);

  return {
    // 상태
    adminId,
    password,
    errors,
    securityEvents,
    
    // 함수
    setAdminId: setAdminIdSafe,
    setPassword: setPasswordSafe,
    validateLoginData,
    resetAuthForm,
    
    // 계산된 상태
    isValid: Object.keys(errors).length === 0 && adminId.trim() && password.trim(),
    hasSecurityRisks: securityEvents.length > 0
  };
};

/**
 * 실시간 입력 보안 모니터링 훅
 */
export const useSecurityMonitor = () => {
  const [securityLog, setSecurityLog] = useState([]);
  const [riskLevel, setRiskLevel] = useState('LOW');

  const logSecurityEvent = useCallback((event) => {
    const logEntry = {
      ...event,
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    setSecurityLog(prev => [...prev.slice(-99), logEntry]); // 최근 100개만 유지

    // 위험도 레벨 업데이트
    if (event.riskLevel === 'HIGH') {
      setRiskLevel('HIGH');
    } else if (event.riskLevel === 'MEDIUM' && riskLevel !== 'HIGH') {
      setRiskLevel('MEDIUM');
    }
  }, [riskLevel]);

  const clearSecurityLog = useCallback(() => {
    setSecurityLog([]);
    setRiskLevel('LOW');
  }, []);

  return {
    securityLog,
    riskLevel,
    logSecurityEvent,
    clearSecurityLog,
    eventCount: securityLog.length
  };
};

export default useValidation;
